import React from 'react';
import { Bot, Zap, Clock, Target, CheckCircle, ArrowRight } from 'lucide-react';
import { useSEO } from '../../hooks/useSEO';
import { Breadcrumb } from '../../components/Breadcrumb';
import { useBanner } from '../../contexts/BannerContext';

export const CognitiveAgentsPage: React.FC = () => {
  useSEO('cognitive-agents' as any);
  const { isBannerVisible, bannerHeight } = useBanner();

  return (
    <>
      <Breadcrumb />
      <main className="pb-20">
      <section 
        className="px-4 sm:px-6 lg:px-8 mb-20"
        style={{ 
          paddingTop: isBannerVisible ? `${bannerHeight + 64 + 44 + 16}px` : '124px' // 64px header + 44px breadcrumb + 16px extra space
        }}
      >
        <div className="max-w-7xl mx-auto text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-blue-600 to-blue-700 rounded-3xl mx-auto mb-8 flex items-center justify-center">
            <Bot className="w-10 h-10 text-white" />
          </div>
          <h1 
            className="text-5xl md:text-6xl font-bold mb-8 py-4 px-2 leading-loose bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent overflow-visible min-h-fit"
            style={{ lineHeight: '1.8', minHeight: 'auto', overflow: 'visible', paddingTop: '16px', paddingBottom: '16px' }}
          >
            Cognitive Agents
          </h1>
          <p className="text-xl text-gray-700 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Transform your business operations with intelligent AI agents and robotic process automation 
            designed to enhance productivity while reducing operational costs and minimizing human error.
          </p>
        </div>
      </section>

      <div className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <section className="grid lg:grid-cols-2 gap-12 mb-16">
            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">AI Automation Solutions</h2>
              <div className="space-y-6">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Robotic Process Automation (RPA)</h3>
                    <p className="text-gray-700 dark:text-gray-300">Automate repetitive tasks and workflows with intelligent bots that work 24/7.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Intelligent Document Processing</h3>
                    <p className="text-gray-700 dark:text-gray-300">Extract and process data from documents with AI-powered accuracy.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Workflow Automation</h3>
                    <p className="text-gray-700 dark:text-gray-300">Streamline complex business processes with intelligent workflow orchestration.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mr-3" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Low-Code/No-Code Platforms</h3>
                    <p className="text-gray-700 dark:text-gray-300">Democratize automation with user-friendly platforms for non-technical users.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">Key Benefits</h2>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <Zap className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">Enhanced</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">Productivity</div>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <Clock className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">24/7</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">Automated Operations</div>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <Target className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">High</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">Accuracy</div>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center">
                    <ArrowRight className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">Cost</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">Optimization</div>
                </div>
              </div>
            </div>
          </section>

          <section className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8 mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Use Cases</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <h3 className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-4">Finance & Accounting</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Automate invoice processing, expense reporting, financial reconciliation, and compliance reporting with AI-powered accuracy.
                </p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-4">Human Resources</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Streamline employee onboarding, payroll processing, performance management, and candidate screening workflows.
                </p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-blue-600 dark:text-blue-400 mb-4">Customer Service</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  Enhance customer support with automated ticket routing, response generation, and case management systems.
                </p>
              </div>
            </div>
          </section>

          <section className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              Ready to Automate Your Business?
            </h2>
            <p className="text-gray-700 dark:text-gray-300 text-lg mb-8 max-w-3xl mx-auto">
              Transform your operations with intelligent automation solutions designed to deliver 
              measurable business results and competitive advantage.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => window.location.href = '/contact'}
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                Start Your Automation Journey
              </button>
              <button
                onClick={() =>
                  window.open(
                    "https://outlook.office.com/book/CognitiveCraftHub2@cognitivecrafthub.com/?ismsaljsauthenabled",
                    "_blank"
                  )
                }
                className="px-8 py-4 border-2 border-gray-300 dark:border-white/30 text-gray-700 dark:text-white font-semibold rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 hover:border-gray-400 dark:hover:border-white/50 transition-all duration-300"
              >
                Schedule Demo
              </button>
            </div>
          </section>
        </div>
      </div>
    </main>
    </>
  );
};